---
title: Confidentialité
updated: 2026-10-02
---

## 1. Responsable du Traitement

Draughts Game (« nous ») est le responsable du traitement des données personnelles collectées via cette plateforme. Contact : [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com)

## 2. Données que Nous Collectons

### 2.1 Données de Compte

- **Nom d'utilisateur** - choisi par vous, visible publiquement
- **Adresse e-mail** - chiffrée au repos avec AES-256-GCM, utilisée pour l'identification du compte, non visible publiquement
- **Mot de passe** - stocké sous forme de hachage bcrypt (facteur de coût 12), jamais en clair
- **Confirmation d'âge** - nous enregistrons que vous avez confirmé avoir au moins 16 ans lors de l'inscription
- **Statut de vérification de l'e-mail** - si vous avez confirmé votre adresse e-mail, ainsi que des jetons temporaires de vérification et de réinitialisation de mot de passe
- **Personnalisation du profil (facultatif)** - avatar et pays, choisis par vous, visibles publiquement

### 2.2 Données de Jeu

- Coups, résultats et horodatages des parties
- Classements et statistiques des joueurs
- Participation aux tournois et résultats
- Tentatives de problèmes, classement problèmes et séries de résolution (streaks)
- Votre bibliothèque d'étude : notes de position (texte libre que vous écrivez), dossiers, parties enregistrées et positions enregistrées dans l'éditeur — privées, visibles de vous seul

### 2.3 Données Techniques

- Adresse IP (à des fins de sécurité et de lutte contre la triche)
- Chaîne user-agent du navigateur
- Jetons de session

### 2.4 Demandes d'Assistance

Si vous nous contactez via le formulaire d'assistance, nous collectons votre adresse e-mail, un nom facultatif, ainsi que l'objet et le contenu de votre message, afin de pouvoir traiter votre demande et y répondre.

## 3. Base Juridique du Traitement

Nous traitons vos données personnelles sur la base de :

- **Exécution du contrat** (art. 6(1)(b) RGPD) - pour fournir le service de jeu auquel vous vous êtes inscrit, y compris les e-mails transactionnels (vérification du compte, réinitialisation du mot de passe) et les réponses d'assistance.
- **Intérêts légitimes** (art. 6(1)(f) RGPD) - pour la sécurité de la plateforme, la lutte contre la triche, les statistiques agrégées sur le jeu (quelles variantes sont jouées, comment les parties se terminent) et l'amélioration du service.
- **Consentement** (art. 6(1)(a) RGPD) - pour la mesure d'audience via Umami et le suivi des erreurs via Sentry. Ni l'un ni l'autre ne démarre tant que vous n'avez pas accepté dans la bannière.

## 4. Comment Nous Utilisons Vos Données

- Fourniture et exploitation de la plateforme de jeu
- Authentification de votre compte et maintien des sessions
- Calcul et affichage des classements des joueurs
- Matchmaking et organisation de tournois
- Détection et prévention de la triche et des abus
- Envoi d'e-mails transactionnels (vérification du compte, réinitialisation du mot de passe)
- Traitement de vos demandes d'assistance et réponses à celles-ci
- Amélioration du service grâce à des statistiques agrégées et anonymes
- L'analyse des données de jeu (par ex. variantes jouées, résultats) sous forme agrégée et anonymisée pour comprendre l'utilisation et améliorer la plateforme

## 5. Cookies et Services Tiers

Nous utilisons des cookies essentiels pour l'authentification et vos préférences. Nous nous appuyons sur les services suivants :

- **Umami** (statistiques) - auto-hébergé sur notre propre infrastructure dans l'UE. Sans cookie, il ne démarre que si vous acceptez les statistiques dans la bannière. Les visites sont identifiées par une empreinte pseudonyme, dérivée de votre adresse IP et de votre navigateur et renouvelée chaque jour ; les chiffres ne sont utilisés que de façon agrégée, et vous n'êtes pas suivi d'un site à l'autre.
- **Sentry** (suivi des erreurs) - activé uniquement avec votre consentement.
- **Resend** (envoi d'e-mails) - achemine nos e-mails transactionnels (vérification du compte, réinitialisation du mot de passe) et les notifications d'assistance.
- **DigitalOcean** (hébergement) - centre de données UE (Francfort).

Nous ne vendons pas vos données et n'utilisons pas de traceurs publicitaires.

## 6. Conservation des Données

- **Données de compte** - conservées tant que votre compte est actif.
- **Comptes invités** - supprimés automatiquement après 30 jours d'inactivité.
- **Comptes non vérifiés** - si vous créez un compte sans jamais confirmer votre adresse e-mail, il est supprimé automatiquement après 30 jours d'inactivité.
- **Jetons de rafraîchissement** - expirent après 7 jours (90 jours pour les comptes invités, afin de retrouver vos parties sans mot de passe), nettoyés automatiquement toutes les heures. L'adresse IP et les informations du navigateur liées à une session sont effacées au bout de 7 jours, même si la session dure plus longtemps.
- **Jetons de vérification d'e-mail** - expirent après 24 heures.
- **Jetons de réinitialisation de mot de passe** - expirent après 1 heure.
- **Demandes d'assistance** - conservées le temps nécessaire au traitement de votre demande.
- **Signalements d'abus** - lorsqu'un joueur signale quelqu'un pour abus dans le chat, le chat de cette partie (jusqu'à 100 messages, des deux joueurs) est conservé pour modération et supprimé automatiquement après 90 jours. En dehors de ce cas, le chat n'est jamais conservé.
- **Données de jeu** - conservées indéfiniment dans le cadre du registre public des parties.
- **Bibliothèque d'étude** - les notes de position, les dossiers, les parties enregistrées et les positions enregistrées sont conservés jusqu'à ce que vous les supprimiez, et sont en tout cas supprimés avec votre compte.
- **Données d'erreur Sentry** - supprimées automatiquement après 90 jours.
- **Comptes supprimés** - les données personnelles sont supprimées immédiatement. Les enregistrements de parties sont anonymisés (référence au joueur supprimée).

## 7. Vos Droits (RGPD)

En vertu du Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :

- **Accès** (art. 15) - téléchargez une copie de vos données personnelles depuis les paramètres de votre profil (« Exporter mes données »).
- **Rectification** (art. 16) - modifiez votre mot de passe dans les paramètres du profil. Pour d'autres corrections, contactez-nous.
- **Effacement** (art. 17) - supprimez votre compte depuis les paramètres du profil (« Supprimer le compte »). Cette opération est immédiate et irréversible.
- **Portabilité** (art. 20) - exportez vos données au format JSON lisible par machine depuis les paramètres du profil.
- **Limitation** (art. 18) - demandez la limitation du traitement en nous contactant.
- **Opposition** (art. 21) - refuser les statistiques dans la bannière de cookies désactive **à la fois** la mesure d'audience Umami **et** le suivi des erreurs Sentry. Vous pouvez changer d'avis à tout moment depuis l'interrupteur dans les paramètres de votre profil.

Pour exercer l'un de ces droits, utilisez les outils en libre-service dans les paramètres de votre profil, ou contactez-nous à [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com). Nous répondrons sous 30 jours.

## 8. Sécurité des Données

Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles, notamment le chiffrement des données sensibles au repos et en transit, le hachage sécurisé des mots de passe et la limitation de débit sur les points d'accès d'authentification.

## 9. Transferts Internationaux

Vos données sont principalement stockées et traitées dans l'Espace Économique Européen (EEE), dans le centre de données DigitalOcean de Francfort. Les données d'erreur Sentry peuvent être transférées aux États-Unis dans le cadre de la décision d'adéquation du Data Privacy Framework UE-États-Unis (juillet 2023). Vous pouvez désactiver Sentry dans les paramètres de votre profil. Les e-mails transactionnels sont acheminés via Resend (États-Unis) ; ce transfert est couvert par un accord de traitement des données incluant des clauses contractuelles types.

## 10. Protection des Mineurs

Le Service n'est pas destiné aux enfants de moins de 16 ans (art. 8 RGPD). Nous exigeons une confirmation d'âge lors de l'inscription. Nous ne collectons pas sciemment de données personnelles d'enfants de moins de 16 ans. Si nous apprenons que nous avons collecté des données d'un enfant de moins de 16 ans, nous les supprimerons rapidement.

## 11. Modifications de cette Politique

Nous pouvons mettre à jour cette Politique de Confidentialité de temps à autre. Nous informerons les utilisateurs enregistrés des changements importants via un avis sur la plateforme. La date de « Dernière mise à jour » en haut indique la révision la plus récente.

## 12. Autorité de Contrôle

Vous avez le droit de déposer une plainte auprès de l'autorité de contrôle compétente en matière de protection des données si vous estimez que vos droits ont été violés.

## 13. Contact

Pour toute question concernant cette Politique de Confidentialité :

- E-mail : [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com)
